// Web push: commissions and referrals in a closed tab. The Blazor app's PushService and pushInterop.js.
//
// The Firebase SDK is Google's own FCM client, not part of this app, and only the people who allow notifications need
// it: it loads from Google's CDN on first use, never on a page load. Everything here is best effort - a cabinet that
// cannot do push must still work perfectly, so no failure here reaches the screen.

import { cabinetApi } from './cabinet-api.ts';
import { config } from './config.ts';
import { log } from './logs.ts';
import { state } from './state.ts';

const SDK_VERSION = '12.18.0';
const SERVICE_WORKER_PATH = '/firebase-messaging-sw.js';
const DEFAULT_LOCALE = 'en';

let sdkPromise: Promise<FirebaseCompat> | null = null;
let registrationPromise: Promise<ServiceWorkerRegistration> | null = null;
let messaging: FirebaseMessagingCompat | null = null;

/** What the backend last accepted: the locale is part of it, since every push is rendered in the row's locale. */
let registered: { token: string; locale: string } | null = null;

const firebaseConfig = (): Record<string, string> => ({
    apiKey: config.firebase.apiKey,
    authDomain: config.firebase.authDomain,
    projectId: config.firebase.projectId,
    messagingSenderId: config.firebase.messagingSenderId,
    appId: config.firebase.appId
});

const isConfigured = (): boolean => {
    const f = config.firebase;

    return !!(f.apiKey && f.projectId && f.messagingSenderId && f.appId && f.vapidKey);
};

export const isSupported = (): boolean =>
    'serviceWorker' in navigator && 'Notification' in window && 'PushManager' in window;

function loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');

        script.src = src;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load ' + src));
        document.head.appendChild(script);
    });
}

/** firebase-app, then firebase-messaging, which registers itself onto the namespace the first one creates. */
function loadSdk(): Promise<FirebaseCompat> {
    if (!sdkPromise) {
        sdkPromise = (window.firebase?.messaging
            ? Promise.resolve()
            : loadScript(`https://www.gstatic.com/firebasejs/${SDK_VERSION}/firebase-app-compat.js`)
                .then(() => loadScript(`https://www.gstatic.com/firebasejs/${SDK_VERSION}/firebase-messaging-compat.js`)))
            .then(() => window.firebase!)
            .catch((error: unknown) => {
                sdkPromise = null;
                throw error;
            });
    }

    return sdkPromise;
}

function waitForActivation(registration: ServiceWorkerRegistration): Promise<void> {
    return new Promise<void>(resolve => {
        const pending = registration.installing || registration.waiting;

        if (registration.active || !pending) {
            resolve();

            return;
        }

        const onStateChange = () => {
            if (pending.state === 'activated' || pending.state === 'redundant') {
                pending.removeEventListener('statechange', onStateChange);
                resolve();
            }
        };

        pending.addEventListener('statechange', onStateChange);
    });
}

/** The worker, registered and active. Its configuration rides on its URL: a worker has no window to read it from. */
async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    if (!registrationPromise) {
        const query = new URLSearchParams(firebaseConfig()).toString();

        registrationPromise = navigator.serviceWorker
            .register(`${SERVICE_WORKER_PATH}?${query}`, { scope: '/' })
            .then(async registration => {
                await waitForActivation(registration);

                return registration;
            })
            .catch((error: unknown) => {
                registrationPromise = null;
                throw error;
            });
    }

    return registrationPromise;
}

/** The current FCM token, or null; never prompts. */
async function readToken(): Promise<string | null> {
    if (!isSupported() || Notification.permission !== 'granted') return null;

    try {
        const firebase = await loadSdk();

        if (!messaging) {
            if (!firebase.apps.length) firebase.initializeApp(firebaseConfig());
            messaging = firebase.messaging!();
        }

        return await messaging.getToken({
            vapidKey: config.firebase.vapidKey,
            ...(registrationPromise ? { serviceWorkerRegistration: await registrationPromise } : {})
        });
    } catch (error) {
        console.error('[push] could not read the FCM token', error);

        return null;
    }
}

async function uploadToken(): Promise<void> {
    const token = await readToken();

    if (!token) return;

    const locale = state.locale || DEFAULT_LOCALE;

    if (registered && registered.token === token && registered.locale === locale) return;

    const response = await cabinetApi('push-devices', {
        method: 'PUT',
        json: {
            token,
            platform: 'Web',
            locale,
            timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
    });

    if (response.ok) {
        registered = { token, locale };
    } else {
        log.warn(`Registering the push device returned ${response.status}`, undefined, 'Lify.Influencer.Services.Realization.PushService');
    }
}

/**
 * Asks once for permission and registers the token. Called on every signed-in load: a browser asks at most once per
 * origin and then just answers, which makes this both the opt-in and the refresh a rotated FCM token needs.
 */
export async function requestPermissionAndRegister(): Promise<NotificationPermission | 'unsupported'> {
    try {
        if (!isConfigured()) {
            log.warn('Web push is disabled: FirebaseSettings is incomplete (ApiKey/AppId/VapidKey must all be set)',
                undefined, 'Lify.Influencer.Services.Realization.PushService');

            return 'unsupported';
        }

        if (!isSupported()) return 'unsupported';

        // Before the prompt and whatever its answer: an inactive worker cannot route a notification's click.
        await registerServiceWorker().catch((error: unknown) => console.error('[push] service worker registration failed', error));

        const permission = await Notification.requestPermission();

        if (permission === 'granted') await uploadToken();

        return permission;
    } catch (error) {
        log.warn(`Push registration failed: ${error instanceof Error ? error.message : String(error)}`, undefined, 'Lify.Influencer.Services.Realization.PushService');

        return 'unsupported';
    }
}

/** Sign-out: the backend row goes while the bearer still works, so a shared browser stops showing one person's earnings. */
export async function unregister(): Promise<void> {
    if (!isConfigured()) return;

    try {
        const token = registered?.token ?? await readToken();

        registered = null;

        if (token) {
            const response = await cabinetApi(`push-devices/${encodeURIComponent(token)}`, { method: 'DELETE' });

            if (!response.ok) {
                log.warn(`Unregistering the push device returned ${response.status}`, undefined,
                    'Lify.Influencer.Services.Realization.PushService');
            }
        }

        if (messaging) await messaging.deleteToken();
    } catch (error) {
        log.warn(`Failed to unregister the push device: ${error instanceof Error ? error.message : String(error)}`, undefined,
            'Lify.Influencer.Services.Realization.PushService');
    }
}
